import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import notify from 'devextreme/ui/notify';

export interface BackendErrorMessage {
    key: string; // "ITEM_NOT_FOUND"
    message: string; // "Item {0} wurde nicht gefunden."
    parameters: readonly string[];
}

export interface BackendError extends BackendErrorMessage {
    additionalMessages: BackendErrorMessage[];
}

export function isBackendError(error: unknown): error is BackendError {
    return (
        typeof error === 'object' &&
        error !== null &&
        'key' in error &&
        'message' in error &&
        'parameters' in error &&
        'additionalMessages' in error
    );
}

export function toBackendError(error: unknown): BackendError {
    if (isBackendError(error)) {
        return error;
    }

    if (typeof error === 'object' && error !== null) {
        const obj = error as Record<string, unknown>;

        // HttpErrorResponse – recurse into the response body
        if ('error' in obj && 'status' in obj) {
            return toBackendError(obj['error']);
        }
    }

    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';

    return { key: '', message, parameters: [], additionalMessages: [] };
}

@Injectable({
    providedIn: 'root',
})
export class NotifyService {
    private readonly translocoService = inject(TranslocoService);

    notifySuccess(message: string, options?: { displayTime?: number; width?: number }): void {
        notify({
            message: this.translocoService.translate(message),
            type: 'success',
            displayTime: options?.displayTime ?? 3500,
            width: options?.width,
        });
    }

    notifyError(error: unknown, options?: { displayTime?: number; width?: number }): void {
        const message = this.buildValidationErrorMessage(error);

        notify({
            message,
            type: 'error',
            displayTime: options?.displayTime ?? 3500,
            width: options?.width,
        });
    }

    notifyErrorText(message: string, options?: { displayTime?: number; width?: number }): void {
        notify({
            message,
            type: 'error',
            displayTime: options?.displayTime ?? 3500,
            width: options?.width,
        });
    }

    buildValidationErrorMessage(err: unknown, errorMsg?: string): string {
        const error = toBackendError(err);
        let errorMessage = errorMsg ?? '';

        if (error.additionalMessages?.length > 0) {
            if (error.additionalMessages?.length > 1) errorMessage += '\n';
            error.additionalMessages.forEach(msg => {
                errorMessage += this.translateErrorKey(msg.key, msg.message, msg.parameters) + '\n';
            });
        } else {
            errorMessage = this.translateErrorKey(error.key, error.message, error.parameters) + '\n';
        }

        return errorMessage;
    }

    translateErrorKey(key: string, defaultMessage: string, parameters: readonly string[] = []): string {
        const paramsObj = this.toTranslocoParams(parameters);
        const translated = this.translocoService.translate('ERROR_' + key, paramsObj);

        if (translated !== `ERROR_${key}`) return translated;

        // fallback: defaultMessage + replace {0}/{1}...
        return this.formatIndexPlaceholders(defaultMessage, parameters);
    }

    toTranslocoParams(parameters: readonly string[]): Record<string, string> {
        // remove {} and turn array into object
        return parameters.reduce<Record<string, string>>((acc, value, i) => {
            acc[String(i)] = String(value).replace(/[{}]/g, '');
            return acc;
        }, {});
    }

    formatIndexPlaceholders(defaultMessage: string, parameters: readonly string[]): string {
        return parameters.reduce((msg, value, i) => msg.replaceAll(`{${i}}`, String(value)), defaultMessage);
    }

    isBackendError(error: unknown): error is BackendError {
        return (
            typeof error === 'object' &&
            error !== null &&
            'key' in error &&
            'message' in error &&
            'parameters' in error &&
            'additionalMessages' in error
        );
    }
}
